import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Tab, Tabs, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import type { Block } from '../../../Courses/editors/BlockEditor/types';
import { gridTemplate } from '../../../Courses/editors/BlockEditor/blockUtils';
import { BlockList } from './BlockPlayer';

/* Layout block players: render nested Block[] recursively through BlockList. */

export const ColumnsPlayer: React.FC<{ columns: { width?: string; blocks: Block[] }[] }> = ({ columns }) => {
    if (!columns || columns.length === 0) return null;
    return (
        <Box className="oa-bp-columns" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: gridTemplate(columns) }, gap: 3 }}>
            {columns.map((col, i) => (
                <div key={i} className="oa-bp-column"><BlockList blocks={col.blocks ?? []} /></div>
            ))}
        </Box>
    );
};

export const AccordionPlayer: React.FC<{ items: { title: string; blocks: Block[]; defaultOpen?: boolean }[]; allowMultiple?: boolean }> = ({ items, allowMultiple = false }) => {
    const [open, setOpen] = useState<Set<number>>(() => new Set((items ?? []).map((it, i) => (it.defaultOpen ? i : -1)).filter((i) => i >= 0)));
    if (!items || items.length === 0) return null;
    const toggle = (i: number) =>
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else {
                if (!allowMultiple) next.clear();
                next.add(i);
            }
            return next;
        });
    return (
        <div className="oa-bp-accordion">
            {items.map((item, i) => (
                <Accordion key={i} expanded={open.has(i)} onChange={() => toggle(i)} disableGutters elevation={0} square>
                    <AccordionSummary expandIcon={<ExpandMore />}><Typography sx={{ fontWeight: 600 }}>{item.title}</Typography></AccordionSummary>
                    <AccordionDetails><BlockList blocks={item.blocks ?? []} /></AccordionDetails>
                </Accordion>
            ))}
        </div>
    );
};

export const TabsPlayer: React.FC<{ tabs: { label: string; blocks: Block[] }[] }> = ({ tabs }) => {
    const [active, setActive] = useState(0);
    if (!tabs || tabs.length === 0) return null;
    const current = Math.min(active, tabs.length - 1);
    return (
        <div className="oa-bp-tabs">
            <Tabs value={current} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: '1px solid var(--oa-divider)' }}>
                {tabs.map((t, i) => <Tab key={i} label={t.label} sx={{ textTransform: 'none', fontWeight: 600 }} id={`oa-bp-tab-${i}`} aria-controls={`oa-bp-tabpanel-${i}`} />)}
            </Tabs>
            <div role="tabpanel" id={`oa-bp-tabpanel-${current}`} aria-labelledby={`oa-bp-tab-${current}`} className="oa-bp-tabpanel">
                <BlockList blocks={tabs[current].blocks ?? []} />
            </div>
        </div>
    );
};
